import { Ref, forwardRef } from "react"
import { Formik, FormikHelpers, FormikProps } from "formik"
import * as yup from "yup"
import { StandardFormikErrorScrollWrapper } from "../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../currency/currencyLib"
import { monetaryFieldSchema } from "../../../../../shared/components/forms/monetaryFieldValidation"
import { CreateUpdateProductFormContent } from "./CreateUpdateProductFormContent"
import { sortWeekDays } from "../helpers"
import { WeekDays } from "../../productGroup/productGroupTypes"
import {
    CreateProduct,
    CreateUpdateProductFormValues,
    StockTransactionType,
    StockUpdateInventory,
} from "../productsTypes"
import { PriceListType } from "../../priceList/priceListApi"

type CreateUpdateProductFormProps = Readonly<{
    initialValues?: CreateUpdateProductFormValues | null
    defaultPriceList?: PriceListType | null
    onSubmit: (formData: CreateProduct) => void
}>
type FormikRef = Ref<FormikProps<CreateUpdateProductFormValues>>

export const CreateUpdateProductForm = forwardRef(
    ({ initialValues, defaultPriceList, onSubmit }: CreateUpdateProductFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()

        const defaultInitialValues = {
            productGroup: null,
            productNumber: "",
            name: "",
            price: undefined,
            currency: "DKK",
            shops: [],
            // priceLists: [],
            priceList: defaultPriceList ?? null,
            description: "",
            isActive: true,
            isAdminOnly: false,
            availabilityDetails: {
                weekDays: [],
                allowPreOrder: true,
            },
            variablePrice: false,
            isPriceHidden: false,
            isLocked: false,
            productGroupCode: undefined,
            itemLimits: null,
            itemsForSalePerDay: undefined,
            stock: null,
        }

        const validationSchema = yup.object({
            productGroup: yup
                .object()
                .shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                })
                .required(l10n.getString("required-field")),
            productNumber: yup.string(),
            name: yup.string().required(l10n.getString("required-field")),
            price: monetaryFieldSchema(
                "positive",
                l10n,
                l10n.getString("product-create-price-field-title"),
            ).when("variablePrice", {
                is: false,
                then: (schema) => schema.required(l10n.getString("required-field")),
            }),
            currency: yup.string(),
            shops: yup.array().of(
                yup.object().shape({
                    id: yup.number(),
                    name: yup.string(),
                    isActive: yup.boolean(),
                }),
            ),
            // priceLists: yup
            //     .array()
            //     .of(
            //         yup.object().shape({
            //             id: yup.number(),
            //             name: yup.string(),
            //             isActive: yup.boolean(),
            //         }),
            //     )
            //     .min(1, l10n.getString("required-field")),
            priceList: yup
                .object()
                .shape({
                    id: yup.number(),
                })
                .nullable()
                .required(l10n.getString("required-field")),
            //add characters limit not to let ppl send very big texts to server, maybe add that character limit to form field like we did in gopay app
            description: yup.string(),
            variablePrice: yup.boolean(),
            isPriceHidden: yup.boolean(),
            isLocked: yup.boolean(),
            isActive: yup.boolean(),
            isAdminOnly: yup.boolean(),
            itemLimits: yup
                .object({
                    maxDailyItemsPerPerson: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(1, l10n.getString("product-create-item-limits-max-error"))
                        .optional(),
                    minItemsPerOrder: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .min(2, l10n.getString("product-create-item-limits-min-error"))
                        .optional(),
                })
                .nullable()
                .test("either-or", l10n.getString("product-create-item-limits-error"), function (value) {
                    if (value === null || value === undefined) return true
                    return !!value.maxDailyItemsPerPerson || !!value.minItemsPerOrder
                }),
            availabilityDetails: yup
                .object({
                    dateInterval: yup
                        .object({
                            from: yup.mixed().nullable().optional(),
                            to: yup.mixed().nullable().optional(),
                        })
                        .optional(),
                    weekDays: yup.array().of(yup.mixed<WeekDays>().oneOf(Object.values(WeekDays))),
                    timeInterval: yup
                        .object({
                            start: yup.mixed().nullable().optional(),
                            end: yup.mixed().nullable().optional(),
                        })
                        .optional(),
                    allowPreOrder: yup.boolean(),
                })
                .test("date-range-valid", l10n.getString("product-create-availability-date-error"), function (value) {
                    const from = value?.dateInterval?.from
                    const to = value?.dateInterval?.to
                    if (!from || !to) return true
                    return from <= to
                })
                .test(
                    "time-range-complete",
                    l10n.getString("product-create-availability-time-incomplete-error"),
                    function (value) {
                        const start = value?.timeInterval?.start
                        const end = value?.timeInterval?.end
                        return Boolean(start) === Boolean(end)
                    },
                )
                .test("time-range-valid", l10n.getString("product-create-availability-time-error"), function (value) {
                    const start = value?.timeInterval?.start
                    const end = value?.timeInterval?.end
                    if (!start || !end) return true
                    return start < end
                }),
            itemsForSalePerDay: yup
                .number()
                .nullable()
                .transform((value) => (isNaN(value) ? undefined : value))
                .min(1, l10n.getString("product-stock-on-sale-per-day-min-error"))
                .optional(),
            stock: yup
                .object({
                    total: yup
                        .number()
                        .nullable()
                        .transform((value) => (isNaN(value) ? undefined : value))
                        .optional(),
                    inventory: yup.mixed<StockUpdateInventory>().optional(),
                    inventoryIncomplete: yup.boolean().optional(),
                })
                .nullable()
                .test("stock-outbound-exceeded", l10n.getString("product-stock-negative-error"), function (stock) {
                    if (!stock?.inventory?.stockUpdate) return true
                    const { transactionType, quantityDelta } = stock.inventory.stockUpdate
                    if (transactionType !== StockTransactionType.DECREASE) return true
                    const total = stock.total ?? 0
                    return quantityDelta <= total
                })
                .test("stock-inventory-incomplete", l10n.getString("required-field"), function (stock) {
                    return !stock?.inventoryIncomplete
                }),
        })

        function handleSubmit(
            values: CreateUpdateProductFormValues,
            _helpers: FormikHelpers<CreateUpdateProductFormValues>,
        ) {
            const {
                productGroup,
                shops,
                price,
                productPhoto,
                productDescription,
                availabilityDetails,
                deadline,
                // priceLists,
                priceList,
                deliveryDetails,
                itemLimits,
                productGroupCode,
                variablePrice,
                isPriceHidden,
                isLocked,
                stock,
                itemsForSalePerDay,
                ...productValues
            } = values

            const parsedItemsForSale = Number(itemsForSalePerDay)
            const hasItemsForSale =
                itemsForSalePerDay != null &&
                String(itemsForSalePerDay).trim() !== "" &&
                !Number.isNaN(parsedItemsForSale) &&
                parsedItemsForSale > 0

            const isStockManagementEnabled = stock != null

            const productGroupCodeBody = productGroupCode
                ? {
                      productGroupCode: {
                          id: productGroupCode.id,
                      },
                  }
                : {}

            const modifyData = {
                ...productValues,
                ...productGroupCodeBody,
                productGroup: {
                    id: productGroup?.id!,
                },
                shops: shops.map((shop) => ({
                    id: shop.id,
                })),
                ...(productDescription
                    ? {
                          productDescription: {
                              ...productDescription,
                              allergens: (productDescription.allergens ?? []).map((allergen) => ({
                                  id: allergen.id,
                              })),
                          },
                      }
                    : {}),
                availabilityDetails: {
                    allowPreOrder: !availabilityDetails?.allowPreOrder,
                    ...(availabilityDetails?.weekDays?.length
                        ? { weekDays: sortWeekDays(availabilityDetails.weekDays) }
                        : {}),
                    ...(availabilityDetails?.timeInterval?.start && availabilityDetails.timeInterval.end
                        ? {
                              timeInterval: {
                                  start: dateTimeFactory.toHourMinute(availabilityDetails.timeInterval.start),
                                  end: dateTimeFactory.toHourMinute(availabilityDetails.timeInterval.end),
                              },
                          }
                        : {}),
                    ...(availabilityDetails?.dateInterval?.from || availabilityDetails?.dateInterval?.to
                        ? {
                              dateInterval: {
                                  ...(availabilityDetails?.dateInterval?.from
                                      ? { from: availabilityDetails?.dateInterval?.from.toISODate() }
                                      : {}),
                                  ...(availabilityDetails?.dateInterval?.to
                                      ? { to: availabilityDetails?.dateInterval?.to.toISODate() }
                                      : {}),
                              },
                          }
                        : {}),
                },
                ...(deadline
                    ? {
                          deadline: {
                              purchases: {
                                  ...deadline.purchases,
                                  timeOfDay: deadline.purchases.timeOfDay.toFormat("HH:mm"),
                              },
                              // hide cancellation for now
                              // cancellation: {
                              //     ...deadline.cancellation,
                              //     timeOfDay: deadline.cancellation.timeOfDay.toFormat("HH:mm"),
                              // },
                          },
                      }
                    : {}),
                // ...(priceLists && priceLists.length > 0
                //     ? {
                //           priceLists: priceLists.map((priceList) => ({
                //               id: priceList.id,
                //           })),
                //       }
                //     : {}),
                ...(priceList
                    ? {
                          priceLists: [{ id: priceList.id }],
                      }
                    : {}),
                ...(deliveryDetails
                    ? {
                          deliveryDetails: { location: { id: deliveryDetails?.location.id } },
                      }
                    : {}),
                paymentDetails: {
                    variablePrice,
                    isPriceHidden,
                },
                securitySettings: {
                    isLocked,
                },
                ...(!variablePrice && price != null
                    ? { price: toMonetaryAmount(toDineroLossy(price, toCurrency(values.currency))) }
                    : {}),
                ...(itemLimits && (itemLimits.maxDailyItemsPerPerson || itemLimits.minItemsPerOrder)
                    ? {
                          itemLimits: itemLimits,
                      }
                    : {}),
                ...(isStockManagementEnabled || hasItemsForSale
                    ? {
                          stock: {
                              isActive: isStockManagementEnabled,
                              ...(stock?.inventory ? { inventory: stock.inventory } : {}),
                              ...(hasItemsForSale ? { sales: { daily: { itemsForSale: parsedItemsForSale } } } : {}),
                          },
                      }
                    : {}),
            }

            if (!productPhoto?.uid && productPhoto?.url) {
                onSubmit({
                    ...modifyData,
                    productPhoto: {
                        url: productPhoto?.url,
                    },
                })
            } else {
                onSubmit({
                    ...modifyData,
                    ...(productPhoto?.uid !== undefined
                        ? {
                              productPhoto: {
                                  uid: productPhoto?.uid,
                              },
                          }
                        : {}),
                })
            }
        }

        return (
            <>
                <Formik<CreateUpdateProductFormValues>
                    enableReinitialize
                    initialValues={initialValues ? initialValues : defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                    validateOnBlur={false}
                    validateOnChange={false}
                >
                    <StandardFormikErrorScrollWrapper<CreateUpdateProductFormValues>
                        children={<CreateUpdateProductFormContent isEditMode={!!initialValues} />}
                    />
                </Formik>
            </>
        )
    },
)
